import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  useTheme,
} from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import type { Payload } from 'recharts/types/component/DefaultLegendContent'
import DateRangeFilter from './DateRangeFilter'
import { useState, useEffect, useMemo } from 'react'
import { useCustom } from '@refinedev/core'
import ErrorMessage from './ErrorMessage'
import { rollingAveragesByDate, averageKey } from '../utils/rollingAverage'

interface FunderOppStat {
  type: string
  count: number
}

interface TimeseriesDataPoint {
  date: string
  new_funders: number
  updated_funders: number
  new_opportunities: number
  updated_opportunities: number
}

interface DatabaseStats {
  data: {
    stats_breakdown: FunderOppStat[]
    timeseries_data?: TimeseriesDataPoint[]
  }
}

const SERIES_NAMES = [
  'Funders Created',
  'Funders Updated',
  'Opportunities Created',
  'Opportunities Updated',
]

const ROLLING_WINDOWS = [7, 14, 30, 90]

const getPeriodLabel = (
  startDate: string | undefined,
  endDate: string | undefined,
  selectedPeriod: string
) => {
  if (!startDate || !endDate) {
    return 'All Time'
  }

  // Use selectedPeriod if available to determine the appropriate label
  let currentLabel = ''

  if (selectedPeriod) {
    switch (selectedPeriod) {
      case '7days':
        currentLabel = 'Last 7 Days'
        break
      case '14days':
        currentLabel = 'Last 14 Days'
        break
      case '30days':
        currentLabel = 'Last 30 Days'
        break
      case '90days':
        currentLabel = 'Last 90 Days'
        break
      case 'thisweek':
        currentLabel = 'This Week'
        break
      case 'lastweek':
        currentLabel = 'Last Week'
        break
      case 'thismonth':
        currentLabel = 'This Month'
        break
      case 'lastmonth':
        currentLabel = 'Last Month'
        break
      case 'thisquarter':
        currentLabel = 'This Quarter'
        break
      case 'lastquarter':
        currentLabel = 'Last Quarter'
        break
      default:
        currentLabel = 'Current Period'
    }
  } else {
    // Show actual date range when no period is selected
    if (startDate && endDate) {
      const formatDisplayDate = (dateStr: string) => {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-NZ', {
          month: 'short',
          day: 'numeric',
        })
      }
      currentLabel = `${formatDisplayDate(startDate)} - ${formatDisplayDate(
        endDate
      )}`
    } else {
      currentLabel = 'Custom Period'
    }
  }

  return currentLabel
}

export default function DatabaseStats() {
  const theme = useTheme()

  // Date filter state
  // Set default to last 14 days
  const getDefaultDates = () => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const twoWeeksAgo = new Date(today)
    twoWeeksAgo.setDate(today.getDate() - 13)

    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return {
      start: formatLocalDate(twoWeeksAgo),
      end: formatLocalDate(today),
    }
  }

  const defaultDates = getDefaultDates()
  const [startDate, setStartDate] = useState(defaultDates.start)
  const [endDate, setEndDate] = useState(defaultDates.end)
  const [appliedFilters, setAppliedFilters] = useState<{
    start_date?: string
    end_date?: string
  }>({
    start_date: defaultDates.start,
    end_date: defaultDates.end,
  })
  const [selectedPeriod, setSelectedPeriod] = useState<string>('14days')

  const [fundersCreated, setFundersCreated] = useState<number>(0)
  const [fundersUpdated, setFundersUpdated] = useState<number>(0)
  const [opportunitiesCreated, setOpportunitiesCreated] = useState<number>(0)
  const [opportunitiesUpdated, setOpportunitiesUpdated] = useState<number>(0)



  const handleQuickFilter = (start: string, end: string) => {
    setStartDate(start)
    setEndDate(end)
    const newFilters: { start_date?: string; end_date?: string } = {}
    if (start) newFilters.start_date = start
    if (end) newFilters.end_date = end
    setAppliedFilters(newFilters)
  }

  // Handlers for manual date changes that clear selected period
  const handleManualStartDateChange = (date: string) => {
    setStartDate(date)
    setSelectedPeriod('') // Clear period selection when manually changing dates
  }

  const handleManualEndDateChange = (date: string) => {
    setEndDate(date)
    setSelectedPeriod('') // Clear period selection when manually changing dates
  }

  const handleApplyFilter = () => {
    const newFilters: { start_date?: string; end_date?: string } = {}
    if (startDate) newFilters.start_date = startDate
    if (endDate) newFilters.end_date = endDate
    setAppliedFilters(newFilters)
    // Clear selectedPeriod when applying custom dates
    setSelectedPeriod('')
  }

  const handleClearFilter = () => {
    setStartDate('')
    setEndDate('')
    setAppliedFilters({})
    setSelectedPeriod('') // Clear period selection when clearing filters
  }

  const periodLabel = getPeriodLabel(startDate, endDate, selectedPeriod)

  // State for timeseries data
  const [timeseriesData, setTimeseriesData] = useState<TimeseriesDataPoint[]>(
    []
  )

  // State for toggling line visibility
  const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set())

  // Rolling-average windows the user has ticked; none by default
  const [averageWindows, setAverageWindows] = useState<Set<number>>(new Set())

  const seriesColors: Record<string, string> = {
    'Funders Created': theme.palette.success.light,
    'Funders Updated': theme.palette.warning.light,
    'Opportunities Created': theme.palette.info.light,
    'Opportunities Updated': theme.palette.secondary.main,
  }

  const toggleAverageWindow = (window: number) => {
    setAverageWindows((prev) => {
      const next = new Set(prev)
      if (next.has(window)) {
        next.delete(window)
      } else {
        next.add(window)
      }
      return next
    })
  }

  // Handle legend click to toggle line visibility
  const handleLegendClick = (data: Payload) => {
    const { dataKey } = data
    if (!dataKey) return
    const keyString = String(dataKey)
    setHiddenLines((prev) => {
      const newHidden = new Set(prev)
      if (newHidden.has(keyString)) {
        newHidden.delete(keyString)
      } else {
        newHidden.add(keyString)
      }
      return newHidden
    })
  }

  // Convert UTC dates to NZ time for display and filtering
  const formatDateToNZ = (utcDateStr: string) => {
    const utcDate = new Date(utcDateStr + 'Z') // Ensure it's treated as UTC
    return utcDate.toLocaleDateString('en-NZ', {
      month: 'short',
      day: 'numeric',
      timeZone: 'Pacific/Auckland',
    })
  }

  // Convert UTC date to NZ date string (YYYY-MM-DD format for comparison)
  const convertUTCToNZDate = (utcDateStr: string): string => {
    const utcDate = new Date(utcDateStr + 'Z') // Ensure it's treated as UTC
    return utcDate.toLocaleDateString('en-CA', {
      // en-CA locale provides YYYY-MM-DD format
      timeZone: 'Pacific/Auckland',
    })
  }

  // Filter timeseries data to show only dates that fall within NZ timezone date range
  const filteredTimeseriesData = useMemo(() => {
    return timeseriesData.filter((point) => {
      if (!appliedFilters.start_date && !appliedFilters.end_date) return true

      const nzDate = convertUTCToNZDate(point.date)
      const startOk =
        !appliedFilters.start_date || nzDate >= appliedFilters.start_date
      const endOk =
        !appliedFilters.end_date || nzDate <= appliedFilters.end_date

      return startOk && endOk
    })
  }, [timeseriesData, appliedFilters.start_date, appliedFilters.end_date])

  // Rolling averages are computed over the *full* history, not the filtered
  // window, so a 90 day average is still correct on the first day of a 90 day
  // view. Zero-filling of absent days happens inside rollingAveragesByDate.
  const rollingAverages = useMemo(() => {
    const rows = timeseriesData.map((point) => ({
      date: convertUTCToNZDate(point.date),
      'Funders Created': point.new_funders,
      'Funders Updated': point.updated_funders,
      'Opportunities Created': point.new_opportunities,
      'Opportunities Updated': point.updated_opportunities,
    }))
    return rollingAveragesByDate(rows, SERIES_NAMES, ROLLING_WINDOWS)
  }, [timeseriesData])

  // Ticked windows, for the series still visible on the legend
  const visibleAverageKeys = ROLLING_WINDOWS.filter((window) =>
    averageWindows.has(window)
  ).flatMap((window) =>
    SERIES_NAMES.filter((series) => !hiddenLines.has(series)).map((series) => ({
      series,
      window,
      dataKey: averageKey(series, window),
    }))
  )

  // Prepare chart data for timeseries
  const chartData = filteredTimeseriesData.map((point) => {
    const averages = rollingAverages[convertUTCToNZDate(point.date)] ?? {}
    const row: Record<string, string | number> = {
      date: formatDateToNZ(point.date),
      'Funders Created': point.new_funders,
      'Funders Updated': point.updated_funders,
      'Opportunities Created': point.new_opportunities,
      'Opportunities Updated': point.updated_opportunities,
    }
    for (const { dataKey } of visibleAverageKeys) {
      row[dataKey] = averages[dataKey] ?? 0
    }
    return row
  })

  // Number of days in the displayed range (inclusive)
  const numberOfDays = useMemo(() => {
    const msPerDay = 1000 * 60 * 60 * 24
    // Prefer explicit filter window (set by quick filters and manual apply)
    if (appliedFilters.start_date && appliedFilters.end_date) {
      const start = new Date(appliedFilters.start_date)
      const end = new Date(appliedFilters.end_date)
      const diff = Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1
      return diff > 0 ? diff : 0
    }
    // Fallback: derive from min/max of available data
    if (filteredTimeseriesData.length > 0) {
      const first = new Date(filteredTimeseriesData[0].date + 'Z')
      const last = new Date(
        filteredTimeseriesData[filteredTimeseriesData.length - 1].date + 'Z'
      )
      const diff = Math.floor((last.getTime() - first.getTime()) / msPerDay) + 1
      return diff > 0 ? diff : 0
    }
    return 0
  }, [filteredTimeseriesData, appliedFilters.start_date, appliedFilters.end_date])

  // Formats the per-day derivative string
  const formatPerDay = (total: number) => {
    if (!numberOfDays) return '0/day'
    const perDay = total / numberOfDays
    const decimals = perDay >= 1 ? 1 : 2
    return `${perDay.toFixed(decimals)}/day`
  }

  // Fetch all data from backend - filtering handled on frontend for NZ timezone
  const queryParams = new URLSearchParams()
  const queryString = queryParams.toString()

  // api call
  const { isLoading, isError } = useCustom({
    url: `/funders/database-stats${queryString && `?${queryString}`}`,
    method: 'get',
    queryOptions: {
      onSuccess: (response: DatabaseStats) => {
        const data = response.data || response

        // Store all timeseries data - frontend will filter by NZ timezone dates
        if (data.timeseries_data) {
          setTimeseriesData(data.timeseries_data)
        }
      },
      onError: () => {},
    },
    meta: {
      queryKey: ['databaseStats'], // No date filters in key since we fetch all data
    },
  })

  // Calculate stats totals from NZ timezone filtered data
  useEffect(() => {
    const totals = filteredTimeseriesData.reduce(
      (acc, point) => ({
        funders_created: acc.funders_created + point.new_funders,
        funders_updated: acc.funders_updated + point.updated_funders,
        opportunities_created:
          acc.opportunities_created + point.new_opportunities,
        opportunities_updated:
          acc.opportunities_updated + point.updated_opportunities,
      }),
      {
        funders_created: 0,
        funders_updated: 0,
        opportunities_created: 0,
        opportunities_updated: 0,
      }
    )

    setFundersCreated(totals.funders_created)
    setFundersUpdated(totals.funders_updated)
    setOpportunitiesCreated(totals.opportunities_created)
    setOpportunitiesUpdated(totals.opportunities_updated)
  }, [filteredTimeseriesData])

  return (
    <>
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {isError && <ErrorMessage />}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          bgcolor: '#F2F2F2',
          p: 2,
          borderRadius: 1,
        }}
      >
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Database Changes
            </Typography>

            <Typography variant="h6" gutterBottom>
              Funders and Opportunities Created and Updated{' '}
              {startDate || endDate
                ? `from ${periodLabel}`
                : `- ${periodLabel}`}
            </Typography>

            <FormGroup row sx={{ alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                Rolling average:
              </Typography>
              {ROLLING_WINDOWS.map((window) => (
                <FormControlLabel
                  key={window}
                  control={
                    <Checkbox
                      size="small"
                      checked={averageWindows.has(window)}
                      onChange={() => toggleAverageWindow(window)}
                    />
                  }
                  label={`${window}d`}
                />
              ))}
            </FormGroup>

            {/* Chart Visualization */}
            {chartData.length > 0 ? (
              <Box
                sx={{
                  height: 400,
                  mb: 3,
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 50, right: 30, left: 0, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number | string) =>
                        typeof value === 'number'
                          ? Number(value.toFixed(1))
                          : value
                      }
                    />
                    <Legend
                      onClick={handleLegendClick}
                      align="center"
                      wrapperStyle={{ paddingLeft: '20px', cursor: 'pointer' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="Funders Created"
                      stroke={
                        hiddenLines.has('Funders Created')
                          ? 'transparent'
                          : seriesColors['Funders Created']
                      }
                      strokeWidth={1.5}
                      dot={
                        hiddenLines.has('Funders Created')
                          ? false
                          : {
                              fill: seriesColors['Funders Created'],
                              r: 1.5,
                            }
                      }
                      name="Funders Created"
                      hide={hiddenLines.has('Funders Created')}
                    />
                    <Line
                      type="monotone"
                      dataKey="Funders Updated"
                      stroke={
                        hiddenLines.has('Funders Updated')
                          ? 'transparent'
                          : seriesColors['Funders Updated']
                      }
                      strokeWidth={1.5}
                      dot={
                        hiddenLines.has('Funders Updated')
                          ? false
                          : {
                              fill: seriesColors['Funders Updated'],
                              r: 1.5,
                            }
                      }
                      name="Funders Updated"
                      hide={hiddenLines.has('Funders Updated')}
                    />
                    <Line
                      type="monotone"
                      dataKey="Opportunities Created"
                      stroke={
                        hiddenLines.has('Opportunities Created')
                          ? 'transparent'
                          : seriesColors['Opportunities Created']
                      }
                      strokeWidth={1.5}
                      dot={
                        hiddenLines.has('Opportunities Created')
                          ? false
                          : {
                              fill: seriesColors['Opportunities Created'],
                              r: 1.5,
                            }
                      }
                      name="Opportunities Created"
                      hide={hiddenLines.has('Opportunities Created')}
                    />
                    <Line
                      type="monotone"
                      dataKey="Opportunities Updated"
                      stroke={
                        hiddenLines.has('Opportunities Updated')
                          ? 'transparent'
                          : seriesColors['Opportunities Updated']
                      }
                      strokeWidth={1.5}
                      dot={
                        hiddenLines.has('Opportunities Updated')
                          ? false
                          : {
                              fill: seriesColors['Opportunities Updated'],
                              r: 1.5,
                            }
                      }
                      name="Opportunities Updated"
                      hide={hiddenLines.has('Opportunities Updated')}
                    />
                    {visibleAverageKeys.map(({ series, window, dataKey }) => (
                      <Line
                        key={dataKey}
                        type="monotone"
                        dataKey={dataKey}
                        stroke={seriesColors[series]}
                        strokeWidth={2}
                        strokeDasharray={window <= 14 ? '6 3' : '2 3'}
                        strokeOpacity={0.75}
                        dot={false}
                        activeDot={false}
                        name={dataKey}
                        legendType="none"
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  No time-series data available for the selected period
                </Typography>
              </Box>
            )}

            {/* Table */}
            <TableContainer sx={{ overflowX: 'auto', mb: 5 }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>
                      <strong>Total Created</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Total Updated</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Funders</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{fundersCreated}</strong>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                        sx={{ ml: 1, fontWeight: 600 }}
                      >
                        ({formatPerDay(fundersCreated)})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <strong>{fundersUpdated}</strong>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                        sx={{ ml: 1, fontWeight: 600 }}
                      >
                        ({formatPerDay(fundersUpdated)})
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Opportunities</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{opportunitiesCreated}</strong>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                        sx={{ ml: 1, fontWeight: 600 }}
                      >
                        ({formatPerDay(opportunitiesCreated)})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <strong>{opportunitiesUpdated}</strong>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                        sx={{ ml: 1, fontWeight: 600 }}
                      >
                        ({formatPerDay(opportunitiesUpdated)})
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleManualStartDateChange}
              onEndDateChange={handleManualEndDateChange}
              onApplyFilter={handleApplyFilter}
              onClearFilter={handleClearFilter}
              onQuickFilter={handleQuickFilter}
              comparePeriods={0}
              onComparePeriodsChange={() => {}}
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
              showComparePeriods={false}
            />
          </CardContent>
        </Card>
      </Box>
    </>
  )
}
